// src/components/Settings.js
import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { API_URL } from './apiurl';
import { toast } from 'react-toastify';

/** Rounds value to quarter hours (0.25 steps) */
function roundToQuarter(value) {
  const floatVal = parseFloat(value);
  if (isNaN(floatVal)) return '';
  const quarterVal = Math.round(floatVal * 4) / 4;
  return quarterVal.toFixed(2);
}

function Settings() {
  const { auth } = useContext(AuthContext);
  const [monthlyHours, setMonthlyHours] = useState('');
  const [bruttoFixum, setBruttoFixum] = useState('');
  const [hotelSpesenCost, setHotelSpesenCost] = useState('');
  const [verpflegungsMehraufwandCost, setVerpflegungsMehraufwandCost] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showInfoTooltip, setShowInfoTooltip] = useState({});

  // Colors for different states
  const [colors, setColors] = useState({
    over: '#3b82f6',
    exact: '#10b981',
    under: '#ef4444',
    urlaub: '#eab308',
    feiertag: '#f97316',
    krank: '#9ca3af',
    neutral: '#e5e7eb',
  });

  // Defaults for sick days, vacation, holidays
  const [hoursForKrank, setHoursForKrank] = useState('0');
  const [hoursForUrlaub, setHoursForUrlaub] = useState('0');
  const [hoursForFeiertag, setHoursForFeiertag] = useState('0');

  /**
   * A dictionary, e.g.:
   * {
   *   "2025": { half: 14, full: 28 },
   *   "2026": { half: 15, full: 30 }
   * }
   */
  const [verpflegungByYear, setVerpflegungByYear] = useState({});
  const [stundenByYear, setStundenByYear] = useState({});
  const [newYear2, setNewYear2] = useState('');
  const [neueHourlyWage, setNewHourlyWage] = useState('');
  const [fachgebietWage, setFachgebietWage] = useState('');

  const [newYearHomeoffice, setNewYearHomeoffice] = useState('');
  const [homeofficeByYear, setHomeofficeByYear] = useState({});
  const [newHomeofficeDayValue, setNewHomeofficeDayValue] = useState('');

  // Input fields for "new year", "half day", "full day"
  const [newYear, setNewYear] = useState('');
  const [newHalfValue, setNewHalfValue] = useState('');
  const [newFullValue, setNewFullValue] = useState('');

  // Current tab
  const [activeTab, setActiveTab] = useState('general');

  useEffect(() => {
    // Load from backend when component mounts
    fetchSettings();
  }, [auth.token]);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/settings`, {
        headers: {
          'Authorization': `Bearer ${auth.token}`,
        },
      });
      const data = await res.json();
      
      if (data) {
        setMonthlyHours(data.monthlyHours || '160');
        setStundenByYear(data.hourlyWage || {});
        setColors(data.colors || colors);
        setHoursForKrank(data.hoursForKrank || '0');
        setHoursForUrlaub(data.hoursForUrlaub || '0');
        setHoursForFeiertag(data.hoursForFeiertag || '0');
        setHotelSpesenCost(data.hotelSpesenCost || '');
        setVerpflegungsMehraufwandCost(data.verpflegungsMehraufwandCost || '');
        setVerpflegungByYear(data.verpflegungByYear || {});
        setBruttoFixum(data.bruttoFixum || '');
        setHomeofficeByYear(data.homeofficeByYear || {});
      }
    } catch (err) {
      console.error('Error loading settings:', err);
      toast.error('Fehler beim Laden der Einstellungen');
    }
  };

  /** Generic handlers */
  const handleInputChange = (setter) => (e) => {
    setter(e.target.value);
  };

  const handleInputBlur = (setter) => (e) => {
    const roundedValue = roundToQuarter(e.target.value);
    setter(roundedValue);
  };

  const addStundenYear = () => {
    const y = parseInt(newYear2, 10);
    if (!y) {
      toast.error('Bitte ein gültiges Jahr eingeben (z.B. 2025).');
      return;
    }

    // Save
    const updated = {
      ...stundenByYear,
      [y]: { stundensatz: neueHourlyWage, fachgebiet: fachgebietWage },
    };
    setStundenByYear(updated);
    saveSettings({hourlyWage: updated});
    
    // Reset inputs
    setNewYear2('');
    setNewHourlyWage('');
    setFachgebietWage('');
    
    toast.success(`Stundensatz für ${y} hinzugefügt`);
  };

  const removeStundenYear = (year) => {
    const updatedStundenByYear = { ...stundenByYear };
    delete updatedStundenByYear[year];
    setStundenByYear(updatedStundenByYear);
    saveSettings({hourlyWage: updatedStundenByYear});
    toast.success(`Stundensatz für ${year} entfernt`);
  };

  const addVerpflegungForYear = () => {
    const y = parseInt(newYear, 10);
    if (!y) {
      toast.error('Bitte ein gültiges Jahr eingeben (z.B. 2025).');
      return;
    }
    
    const halfV = parseFloat(newHalfValue);
    const fullV = parseFloat(newFullValue);
    if (isNaN(halfV) || isNaN(fullV)) {
      toast.error('Bitte gültige Werte für Halber/ganzer Tag eingeben.');
      return;
    }
    
    // Save
    const updated = {
      ...verpflegungByYear,
      [y]: { half: halfV, full: fullV },
    };
    setVerpflegungByYear(updated);
    saveSettings({verpflegungByYear: updated});

    // Reset inputs
    setNewYear('');
    setNewHalfValue('');
    setNewFullValue('');
    
    toast.success(`Verpflegungssatz für ${y} hinzugefügt`);
  };

  const addHomeofficeForYear = () => {
    const y = parseInt(newYearHomeoffice, 10);
    if (!y) {
      toast.error('Bitte ein gültiges Jahr eingeben (z.B. 2025).');
      return;
    }
    
    const dayV = parseFloat(newHomeofficeDayValue);
    if (isNaN(dayV)) {
      toast.error('Bitte gültigen Wert für Tag eingeben.');
      return;
    }
    
    // Save
    const updated = {
      ...homeofficeByYear,
      [y]: {day: dayV },
    };
    setHomeofficeByYear(updated);
    saveSettings({homeofficeByYear: updated});

    // Reset inputs
    setNewYearHomeoffice('');
    setNewHomeofficeDayValue('');
    
    toast.success(`Homeoffice-Pauschale für ${y} hinzugefügt`);
  };

  const removeHomeofficeYear = (yearKey) => {
    const updated = { ...homeofficeByYear };
    delete updated[yearKey];
    setHomeofficeByYear(updated);
    saveSettings({homeofficeByYear: updated});
    toast.success(`Homeoffice-Pauschale für ${yearKey} entfernt`);
  };

  const removeVerpflegungYear = (yearKey) => {
    const updated = { ...verpflegungByYear };
    delete updated[yearKey];
    setVerpflegungByYear(updated);
    saveSettings({verpflegungByYear: updated});
    toast.success(`Verpflegungssatz für ${yearKey} entfernt`);
  };

  // Special day settings
  const handleKrankChange = (e) => {
    const value = e.target.value;
    setHoursForKrank(value);
    localStorage.setItem('hoursForKrank', value);
  };
  
  const handleUrlaubChange = (e) => {
    const value = e.target.value;
    setHoursForUrlaub(value);
    localStorage.setItem('hoursForUrlaub', value);
  };
  
  const handleFeiertagChange = (e) => {
    const value = e.target.value;
    setHoursForFeiertag(value);
    localStorage.setItem('hoursForFeiertag', value);
  };

  // Change color
  const handleColorChange = (key, value) => {
    const updated = { ...colors, [key]: value };
    setColors(updated);
    localStorage.setItem('calendarColors', JSON.stringify(updated));
  };

  // Consolidated function for saving settings
  const saveSettings = async (partialSettings = {}) => {
    setIsSaving(true);
    
    try {
      const settingsToSave = {
        monthlyHours: parseFloat(monthlyHours) || 0,
        hourlyWage: stundenByYear,
        colors,
        hoursForKrank: parseFloat(hoursForKrank) || 0,
        hoursForUrlaub: parseFloat(hoursForUrlaub) || 0,
        hoursForFeiertag: parseFloat(hoursForFeiertag) || 0,
        hotelSpesenCost: parseFloat(hotelSpesenCost) || 0,
        verpflegungByYear,
        bruttoFixum: parseFloat(bruttoFixum) || 0,
        homeofficeByYear,
        ...partialSettings
      };
      
      const res = await fetch(`${API_URL}/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.token}`,
        },
        body: JSON.stringify(settingsToSave),
      });
      
      if (res.ok) {
        // Only show toast if this is a full form submission
        if (Object.keys(partialSettings).length === 0) {
          toast.success('Einstellungen erfolgreich gespeichert!');
        }
      } else {
        toast.error('Fehler beim Speichern der Einstellungen.');
      }
    } catch (err) {
      console.error('Error saving settings:', err);
      toast.error('Serverfehler beim Speichern.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    saveSettings();
  };

  const toggleInfoTooltip = (key) => {
    setShowInfoTooltip(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Tooltip component for help texts
  const InfoTooltip = ({ id, text }) => {
    return (
      <div className="tooltip-container">
        <button 
          type="button" 
          onClick={() => toggleInfoTooltip(id)}
          style={styles.infoButton}
          aria-label="Mehr Information"
        >
          ?
        </button>
        {showInfoTooltip[id] && (
          <div style={styles.tooltip}>
            {text}
            <button 
              style={styles.closeTooltip}
              onClick={() => toggleInfoTooltip(id)}
              aria-label="Schließen"
            >
              ×
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Einstellungen</h2>

      {/* Tab Navigation */}
      <div style={styles.tabsContainer}>
        <div style={styles.tabs}>
          <button
            onClick={() => setActiveTab('general')}
            style={{
              ...styles.tabButton,
              ...(activeTab === 'general' ? styles.activeTabButton : {}),
            }}
          >
            <span style={styles.tabIcon}>⚙️</span> Allgemein
          </button>
          <button
            onClick={() => setActiveTab('expenses')}
            style={{
              ...styles.tabButton,
              ...(activeTab === 'expenses' ? styles.activeTabButton : {}),
            }}
          >
            <span style={styles.tabIcon}>💰</span> Spesen
          </button>
          <button
            onClick={() => setActiveTab('yearly')}
            style={{
              ...styles.tabButton,
              ...(activeTab === 'yearly' ? styles.activeTabButton : {}),
            }}
          >
            <span style={styles.tabIcon}>🗓️</span> Erstattungen
          </button>
          <button
            onClick={() => setActiveTab('colors')}
            style={{
              ...styles.tabButton,
              ...(activeTab === 'colors' ? styles.activeTabButton : {}),
            }}
          >
            <span style={styles.tabIcon}>🎨</span> Farben
          </button>
          <button
            onClick={() => setActiveTab('specialDays')}
            style={{
              ...styles.tabButton,
              ...(activeTab === 'specialDays' ? styles.activeTabButton : {}),
            }}
          >
            <span style={styles.tabIcon}>📅</span> Besondere Tage
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div style={styles.card}>
        <form onSubmit={handleSubmit} style={styles.form}>
          {/* General */}
          {activeTab === 'general' && (
            <div style={styles.tabContentWrapper}>
              <h3 style={styles.sectionTitle}>Allgemeine Einstellungen</h3>
              
              <div style={styles.formSection}>
                <div style={styles.formGroupWithInfo}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>
                      Monatliches Brutto Fixum
                      <span style={styles.required}>*</span>
                    </label>
                    <div style={styles.inputWithAddon}>
                      <input
                        type="number"
                        step="0.25"
                        value={bruttoFixum}
                        onChange={handleInputChange(setBruttoFixum)}
                        onBlur={handleInputBlur(setBruttoFixum)}
                        placeholder="z.B. 3250"
                        style={styles.input}
                        required
                      />
                      <span style={styles.inputAddon}>€</span>
                    </div>
                  </div>
                  <InfoTooltip 
                    id="bruttoFixum" 
                    text="Das monatliche Bruttogehalt, das du vertraglich vereinbart hast."
                  />
                </div>
                
                <div style={styles.formGroupWithInfo}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>
                      Monatliche Sollstunden
                      <span style={styles.required}>*</span>
                    </label>
                    <div style={styles.inputWithAddon}>
                      <input
                        type="number"
                        step="0.25"
                        value={monthlyHours}
                        onChange={handleInputChange(setMonthlyHours)}
                        onBlur={handleInputBlur(setMonthlyHours)}
                        placeholder="z.B. 160"
                        style={styles.input}
                        required
                      />
                      <span style={styles.inputAddon}>Std</span>
                    </div>
                    <span style={styles.hint}>Werte werden in 0,25-Stunden-Schritten gerundet</span>
                  </div>
                  <InfoTooltip 
                    id="monthlyHours" 
                    text="Die Anzahl der Stunden, die du monatlich arbeiten sollst (z.B. 160 Stunden bei 40h/Woche)."
                  />
                </div>
              </div>
              
              <h4 style={styles.subSectionTitle}>
                Jahrbasierte Stundensätze
                <InfoTooltip 
                  id="hourlyRates" 
                  text="Hier kannst du Stundensätze für verschiedene Jahre hinterlegen. Diese werden für die Berechnung von Überstunden verwendet."
                />
              </h4>
              
              <div style={styles.formSection}>
                <div style={styles.formGroupRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Jahr</label>
                    <input
                      type="number"
                      value={newYear2}
                      onChange={(e) => setNewYear2(e.target.value)}
                      placeholder="z.B. 2025"
                      style={styles.input}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Stundensatz</label>
                    <div style={styles.inputWithAddon}>
                      <input
                        type="number"
                        step="0.01"
                        value={neueHourlyWage}
                        onChange={(e) => setNewHourlyWage(e.target.value)}
                        placeholder="130.00"
                        style={styles.input}
                      />
                      <span style={styles.inputAddon}>€</span>
                    </div>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Fachgebiet</label>
                    <div style={styles.inputWithAddon}>
                      <input
                        type="number"
                        step="0.01"
                        value={fachgebietWage}
                        onChange={(e) => setFachgebietWage(e.target.value)}
                        placeholder="150.00"
                        style={styles.input}
                      />
                      <span style={styles.inputAddon}>€</span>
                    </div>
                  </div>
                  <button 
                    type="button" 
                    onClick={addStundenYear} 
                    style={styles.addBtn}
                    disabled={!newYear2 || !neueHourlyWage}
                  >
                    Hinzufügen
                  </button>
                </div>

                {/* List of all saved years */}
                {Object.keys(stundenByYear).length > 0 ? (
                  <div style={styles.tableContainer}>
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          <th>Jahr</th>
                          <th>Stundensatz (€)</th>
                          <th>Fachgebiet (€)</th>
                          <th>Aktion</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(stundenByYear)
                          .sort(([a], [b]) => parseInt(b) - parseInt(a)) // Sort years descending
                          .map(([yearKey, data]) => (
                          <tr key={yearKey}>
                            <td>{yearKey}</td>
                            <td>{data.stundensatz}</td>
                            <td>{data.fachgebiet}</td>
                            <td>
                              <button
                                type="button"
                                onClick={() => removeStundenYear(yearKey)}
                                style={styles.deleteBtn}
                                aria-label={`Löschen ${yearKey}`}
                              >
                                Löschen
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={styles.emptyState}>
                    <p>Noch keine Stundensätze hinzugefügt</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Expenses */}
          {activeTab === 'expenses' && (
            <div style={styles.tabContentWrapper}>
              <h3 style={styles.sectionTitle}>Spesen-Einstellungen</h3>
              <div style={styles.formSection}>
                <div style={styles.formGroupWithInfo}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Hotelspeesen pro Übernachtung</label>
                    <div style={styles.inputWithAddon}>
                      <input
                        type="number"
                        step="0.01"
                        value={hotelSpesenCost}
                        onChange={handleInputChange(setHotelSpesenCost)}
                        onBlur={handleInputBlur(setHotelSpesenCost)}
                        placeholder="z.B. 50.00"
                        style={styles.input}
                      />
                      <span style={styles.inputAddon}>€</span>
                    </div>
                  </div>
                  <InfoTooltip 
                    id="hotelSpesen" 
                    text="Der Pauschalbetrag für Hotelübernachtungen, der in der Abrechnung berücksichtigt wird."
                  />
                </div>
              </div>
            </div>
          )}

          {/* Yearly-based rates */}
          {activeTab === 'yearly' && (
            <div style={styles.tabContentWrapper}>
              <h3 style={styles.sectionTitle}>Jahresabhängige Erstattungen</h3>
              
              <h4 style={styles.subSectionTitle}>
                Verpflegungsmehraufwand
                <InfoTooltip 
                  id="verpflegung" 
                  text="Pauschale für Verpflegungsmehraufwand bei Dienstreisen, die je nach Jahr unterschiedlich sein kann."
                />
              </h4>
              
              <div style={styles.formSection}>
                <div style={styles.formGroupRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Jahr</label>
                    <input
                      type="number"
                      value={newYear}
                      onChange={(e) => setNewYear(e.target.value)}
                      placeholder="z.B. 2025"
                      style={styles.input}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Halber Tag</label>
                    <div style={styles.inputWithAddon}>
                      <input
                        type="number"
                        step="0.01"
                        value={newHalfValue}
                        onChange={(e) => setNewHalfValue(e.target.value)}
                        placeholder="14.00"
                        style={styles.input}
                      />
                      <span style={styles.inputAddon}>€</span>
                    </div>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Ganzer Tag</label>
                    <div style={styles.inputWithAddon}>
                      <input
                        type="number"
                        step="0.01"
                        value={newFullValue}
                        onChange={(e) => setNewFullValue(e.target.value)}
                        placeholder="28.00"
                        style={styles.input}
                      />
                      <span style={styles.inputAddon}>€</span>
                    </div>
                  </div>
                  <button 
                    type="button" 
                    onClick={addVerpflegungForYear} 
                    style={styles.addBtn}
                    disabled={!newYear || !newHalfValue || !newFullValue}
                  >
                    Hinzufügen
                  </button>
                </div>

                {/* List of all saved years */}
                {Object.keys(verpflegungByYear).length > 0 ? (
                  <div style={styles.tableContainer}>
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          <th>Jahr</th>
                          <th>Halber Tag (€)</th>
                          <th>Ganzer Tag (€)</th>
                          <th>Aktion</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(verpflegungByYear)
                          .sort(([a], [b]) => parseInt(b) - parseInt(a)) // Sort years descending
                          .map(([yearKey, data]) => (
                          <tr key={yearKey}>
                            <td>{yearKey}</td>
                            <td>{data.half}</td>
                            <td>{data.full}</td>
                            <td>
                              <button
                                type="button"
                                onClick={() => removeVerpflegungYear(yearKey)}
                                style={styles.deleteBtn}
                                aria-label={`Löschen ${yearKey}`}
                              >
                                Löschen
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={styles.emptyState}>
                    <p>Noch keine Verpflegungssätze hinzugefügt</p>
                  </div>
                )}
              </div>
              
              <h4 style={styles.subSectionTitle}>
                Homeoffice-Pauschale
                <InfoTooltip 
                  id="homeoffice" 
                  text="Die tägliche Pauschale für Arbeit im Homeoffice, die steuerlich absetzbar ist."
                />
              </h4>
              
              <div style={styles.formSection}>
                <div style={styles.formGroupRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Jahr</label>
                    <input
                      type="number"
                      value={newYearHomeoffice}
                      onChange={(e) => setNewYearHomeoffice(e.target.value)}
                      placeholder="z.B. 2025"
                      style={styles.input}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Pauschale pro Tag</label>
                    <div style={styles.inputWithAddon}>
                      <input
                        type="number"
                        step="0.01"
                        value={newHomeofficeDayValue}
                        onChange={(e) => setNewHomeofficeDayValue(e.target.value)}
                        placeholder="6.00"
                        style={styles.input}
                      />
                      <span style={styles.inputAddon}>€</span>
                    </div>
                  </div>
                  <button 
                    type="button" 
                    onClick={addHomeofficeForYear} 
                    style={styles.addBtn}
                    disabled={!newYearHomeoffice || !newHomeofficeDayValue}
                  >
                    Hinzufügen
                  </button>
                </div>

                {/* List of all saved years */}
                {Object.keys(homeofficeByYear).length > 0 ? (
                  <div style={styles.tableContainer}>
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          <th>Jahr</th>
                          <th>Pauschale pro Tag (€)</th>
                          <th>Aktion</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(homeofficeByYear)
                          .sort(([a], [b]) => parseInt(b) - parseInt(a)) // Sort years descending
                          .map(([yearKey, data]) => (
                          <tr key={yearKey}>
                            <td>{yearKey}</td>
                            <td>{data.day}</td>
                            <td>
                              <button
                                type="button"
                                onClick={() => removeHomeofficeYear(yearKey)}
                                style={styles.deleteBtn}
                                aria-label={`Löschen ${yearKey}`}
                              >
                                Löschen
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={styles.emptyState}>
                    <p>Noch keine Homeoffice-Pauschalen hinzugefügt</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Calendar Colors */}
          {activeTab === 'colors' && (
            <div style={styles.tabContentWrapper}>
              <h3 style={styles.sectionTitle}>
                Farbeinstellungen für den Kalender
                <InfoTooltip 
                  id="calendarColors" 
                  text="Diese Farben werden im Kalender verwendet, um verschiedene Zustände sichtbar zu machen."
                />
              </h3>
              
              <div style={styles.colorPickersContainer}>
                <div style={styles.colorPickers}>
                  {Object.entries(colors).map(([key, value]) => {
                    const labelText = {
                      over: 'Überstunden',
                      exact: 'Soll erreicht',
                      under: 'Unterstunden',
                      urlaub: 'Urlaub',
                      feiertag: 'Feiertag',
                      krank: 'Krankheit',
                      neutral: 'Neutral'
                    }[key] || key.charAt(0).toUpperCase() + key.slice(1);
                    
                    return (
                      <div key={key} style={styles.colorGroupCard}>
                        <div style={styles.colorPreview}>
                          <div style={{
                            ...styles.colorSwatch,
                            backgroundColor: value
                          }} />
                        </div>
                        <div style={styles.colorLabel}>
                          <label style={styles.colorPickerLabel}>
                            {labelText}
                          </label>
                          <input
                            type="color"
                            value={value}
                            onChange={(e) => handleColorChange(key, e.target.value)}
                            style={styles.colorInput}
                            aria-label={`Farbe für ${labelText}`}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Special Days Settings */}
          {activeTab === 'specialDays' && (
            <div style={styles.tabContentWrapper}>
              <h3 style={styles.sectionTitle}>
                Stundenvorgaben für besondere Tage
                <InfoTooltip 
                  id="specialDays" 
                  text="Diese Stundenwerte werden automatisch für besondere Tage (Krankheit, Urlaub, Feiertag) im Kalender verwendet."
                />
              </h3>
              
              <div style={styles.formSection}>
                <div style={styles.formGroupRow}>
                  <div style={styles.formGroupWithInfo}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Krankheit</label>
                      <div style={styles.inputWithAddon}>
                        <input
                          type="number"
                          step="0.01"
                          value={hoursForKrank}
                          onChange={handleKrankChange}
                          style={styles.input}
                        />
                        <span style={styles.inputAddon}>Std</span>
                      </div>
                    </div>
                    <InfoTooltip 
                      id="sickHours" 
                      text="Die Anzahl der Stunden, die bei Krankheit automatisch eingetragen werden."
                    />
                  </div>
                  
                  <div style={styles.formGroupWithInfo}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Urlaub</label>
                      <div style={styles.inputWithAddon}>
                        <input
                          type="number"
                          step="0.01"
                          value={hoursForUrlaub}
                          onChange={handleUrlaubChange}
                          style={styles.input}
                        />
                        <span style={styles.inputAddon}>Std</span>
                      </div>
                    </div>
                    <InfoTooltip 
                      id="vacationHours" 
                      text="Die Anzahl der Stunden, die bei Urlaubstagen automatisch eingetragen werden."
                    />
                  </div>
                  
                  <div style={styles.formGroupWithInfo}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Feiertag</label>
                      <div style={styles.inputWithAddon}>
                        <input
                          type="number"
                          step="0.01"
                          value={hoursForFeiertag}
                          onChange={handleFeiertagChange}
                          style={styles.input}
                        />
                        <span style={styles.inputAddon}>Std</span>
                      </div>
                    </div>
                    <InfoTooltip 
                      id="holidayHours" 
                      text="Die Anzahl der Stunden, die bei Feiertagen automatisch eingetragen werden."
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Save Button */}
          <div style={styles.buttonContainer}>
            <button 
              type="submit" 
              style={isSaving ? styles.buttonDisabled : styles.button}
              disabled={isSaving}
            >
              {isSaving ? 'Wird gespeichert...' : 'Alle Einstellungen speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Updated Styles
const styles = {
  container: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '1.5rem',
    fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    backgroundColor: '#f9fafb',
    color: '#111827',
  },
  title: {
    textAlign: 'center',
    marginBottom: '1.5rem',
    fontSize: '1.8rem',
    fontWeight: '700',
    color: '#111827',
  },
  tabsContainer: {
    position: 'relative',
    marginBottom: '1rem',
  },
  tabs: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
    borderBottom: '1px solid #e5e7eb',
    paddingBottom: '0.5rem',
  },
  tabButton: {
    padding: '0.75rem 1.25rem',
    borderRadius: '0.5rem 0.5rem 0 0',
    border: 'none',
    cursor: 'pointer',
    backgroundColor: '#f3f4f6',
    color: '#6b7280',
    transition: 'all 0.2s',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    outline: 'none',
    ':hover': {
      backgroundColor: '#e5e7eb',
    },
  },
  activeTabButton: {
    backgroundColor: '#fff',
    color: '#3b82f6',
    borderTop: '2px solid #3b82f6',
    fontWeight: '600',
  },
  tabIcon: {
    fontSize: '1.1rem',
  },
  tabContentWrapper: {
    animation: 'fadeIn 0.3s ease-in-out',
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: '0.75rem',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    padding: '1.5rem',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
  },
  formSection: {
    marginBottom: '1.5rem',
    padding: '1rem',
    backgroundColor: '#f9fafb',
    borderRadius: '0.5rem',
    border: '1px solid #e5e7eb',
  },
  sectionTitle: {
    margin: '0 0 1rem 0',
    fontSize: '1.25rem',
    color: '#111827',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  subSectionTitle: {
    margin: '1.5rem 0 0.75rem 0',
    fontSize: '1.1rem',
    color: '#4b5563',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.3rem',
    minWidth: '150px',
    flex: '1 1 0',
  },
  formGroupWithInfo: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.5rem',
    flex: '1 1 0',
  },
  formGroupRow: {
    display: 'flex',
    gap: '1rem',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
  },
  label: {
    color: '#4b5563',
    fontWeight: '500',
    fontSize: '0.95rem',
    marginBottom: '0.25rem',
  },
  inputWithAddon: {
    display: 'flex',
    position: 'relative',
    width: '100%',
  },
  input: {
    padding: '0.6rem',
    fontSize: '1rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    width: '100%',
    outline: 'none',
    ':focus': {
      borderColor: '#3b82f6',
      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.25)',
    },
  },
  inputAddon: {
    position: 'absolute',
    right: '0.75rem',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#6b7280',
    pointerEvents: 'none',
  },
  hint: {
    fontSize: '0.8rem',
    color: '#6b7280',
    marginTop: '0.25rem',
  },
  required: {
    color: '#ef4444',
    marginLeft: '0.25rem',
  },
  tableContainer: {
    marginTop: '1rem',
    borderRadius: '0.5rem',
    overflow: 'hidden',
    border: '1px solid #e5e7eb',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.95rem',
  },
  addBtn: {
    backgroundColor: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '0.375rem',
    padding: '0.75rem 1rem',
    cursor: 'pointer',
    fontWeight: '500',
    transition: 'background-color 0.2s',
    height: '42px',
    ':hover': {
      backgroundColor: '#2563eb',
    },
    ':disabled': {
      backgroundColor: '#93c5fd',
      cursor: 'not-allowed',
    },
  },
  deleteBtn: {
    backgroundColor: '#ef4444',
    color: '#fff',
    border: 'none',
    borderRadius: '0.375rem',
    padding: '0.4rem 0.75rem',
    cursor: 'pointer',
    fontSize: '0.85rem',
    transition: 'background-color 0.2s',
    ':hover': {
      backgroundColor: '#dc2626',
    },
  },
  button: {
    backgroundColor: '#3b82f6',
    color: '#fff',
    fontSize: '1rem',
    fontWeight: '600',
    padding: '0.75rem 1.5rem',
    border: 'none',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    width: '100%',
    ':hover': {
      backgroundColor: '#2563eb',
    },
  },
  buttonDisabled: {
    backgroundColor: '#93c5fd',
    color: '#fff',
    fontSize: '1rem',
    fontWeight: '600',
    padding: '0.75rem 1.5rem',
    border: 'none',
    borderRadius: '0.375rem',
    cursor: 'not-allowed',
    width: '100%',
  },
  buttonContainer: {
    marginTop: '1rem',
  },
  emptyState: {
    padding: '2rem',
    textAlign: 'center',
    color: '#6b7280',
    backgroundColor: '#f3f4f6',
    borderRadius: '0.5rem',
    border: '1px dashed #d1d5db',
  },
  colorPickersContainer: {
    marginTop: '1rem',
  },
  colorPickers: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: '1rem',
  },
  colorGroupCard: {
    display: 'flex',
    alignItems: 'center',
    padding: '1rem',
    backgroundColor: '#f9fafb',
    borderRadius: '0.5rem',
    border: '1px solid #e5e7eb',
    transition: 'transform 0.2s, box-shadow 0.2s',
    ':hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    },
  },
  colorPreview: {
    marginRight: '1rem',
  },
  colorSwatch: {
    width: '36px',
    height: '36px',
    borderRadius: '0.25rem',
    border: '1px solid #e5e7eb',
  },
  colorLabel: {
    flex: '1',
  },
  colorPickerLabel: {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: '500',
    color: '#4b5563',
  },
  colorInput: {
    appearance: 'none',
    width: '100%',
    height: '30px',
    padding: '0',
    border: '1px solid #d1d5db',
    borderRadius: '0.25rem',
    cursor: 'pointer',
  },
  infoButton: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    backgroundColor: '#e5e7eb',
    color: '#4b5563',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    fontSize: '0.75rem',
    fontWeight: 'bold',
    marginTop: '5px',
  },
  tooltip: {
    position: 'absolute',
    zIndex: '10',
    backgroundColor: '#374151',
    color: '#fff',
    padding: '0.75rem',
    borderRadius: '0.375rem',
    maxWidth: '250px',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    fontSize: '0.85rem',
    marginTop: '0.5rem',
  },
  closeTooltip: {
    position: 'absolute',
    top: '0.25rem',
    right: '0.25rem',
    background: 'none',
    border: 'none',
    color: '#fff',
    fontSize: '1rem',
    cursor: 'pointer',
  },
};

export default Settings;